/**
 * Formula ufficiale rating 6amico — ospitalità Premium.
 * Peso ispettore/admin 60%, media voti persone (QR GPS) 40%.
 * Arrotondamento a 1 decimale: `Math.round(x * 10) / 10`.
 *
 * Senza recensioni GPS il 40% non è disponibile: il rating coincide con il
 * voto dell'ispettore (vedi {@link calcolaRating6Amico}).
 */

/** Arrotondamento ufficiale a 1 decimale (scala 1–10). */
function round1(value) {
  return Math.round(value * 10) / 10;
}

/**
 * Rating finale 6amico (scala 1–10, 1 decimale).
 * @param {number} votoAdmin — voto ispettore / amministratore (60%)
 * @param {number|null} [mediaVotoPersone] — media recensioni turistiche (40%);
 *   `null`/assente = nessuna recensione GPS ancora raccolta
 * @returns {number} con `mediaVotoPersone` assente restituisce il solo voto ispettore
 * @example calcolaRating6Amico(9.8, 8.5) // 9.3
 * @example calcolaRating6Amico(9.0, null) // 9.0 — in attesa dei voti QR
 */
export function calcolaRating6Amico(votoAdmin, mediaVotoPersone) {
  const admin = Number.isFinite(votoAdmin) ? votoAdmin : 0;
  // Nessuna recensione GPS → il peso del 40% non esiste ancora, quindi il rating
  // resta il voto dell'ispettore. Prima `null` diventava 0 e il rating crollava
  // (es. 9.0 ispettore → 5.4 pubblicato come "rating finale").
  if (!Number.isFinite(mediaVotoPersone)) return round1(admin);
  const pesoAdmin = 0.6;
  const pesoPersone = 0.4;
  const punteggioGrezzo = admin * pesoAdmin + mediaVotoPersone * pesoPersone;
  return round1(punteggioGrezzo);
}

/** @deprecated Alias — usare {@link calcolaRating6Amico} */
export const calcRatingFinale = calcolaRating6Amico;

/**
 * Media voti delle persone da recensioni GPS (1 decimale).
 * @param {import('../components/borgo/borgoTypes').RecensioneUtente[] | null | undefined} recensioni
 * @returns {number|null} `null` quando non ci sono recensioni: nessun voto = nessuna
 *   media, e il chiamante decide il valore di ripiego. Prima tornava `0`, che
 *   abbassava il rating finale invece di segnalare il dato assente.
 */
export function calcMediaVotoPersone(recensioni) {
  if (!recensioni?.length) return null;
  const sum = recensioni.reduce((a, r) => a + (r.votoPersone ?? r.voto ?? 0), 0);
  return round1(sum / recensioni.length);
}

/**
 * Arricchisce {@link import('../components/borgo/borgoTypes').CertificazioneOspitalita} con medie e rating calcolato.
 *
 * In attesa dei voti QR (`inAttesaVotiQR: true`): `mediaVotoPersone` resta un numero
 * (ripiego = voto ispettore, così `toFixed(1)` dei componenti non esplode) e
 * `ratingFinaleCalcolato` coincide con il voto dell'ispettore.
 *
 * @param {object} base — campi ispezione (`votoIspettore`, giudizio, garanzia, …)
 * @param {import('../components/borgo/borgoTypes').RecensioneUtente[]} [votiDellePersone]
 * @returns {import('../components/borgo/borgoTypes').CertificazioneOspitalita}
 */
export function enrichCertificazioneOspitalita(base, votiDellePersone = []) {
  const recensioni = votiDellePersone ?? base.votiDellePersone ?? [];
  const mediaCalcolata = calcMediaVotoPersone(recensioni);
  const inAttesaVotiQR = !recensioni.length;
  // Il campo pubblico resta numerico (contratto borgoTypes): senza voti QR si usa
  // il voto ispettore come segnaposto, non uno 0 fuorviante.
  const mediaVotoPersone = mediaCalcolata ?? base.votoIspettore ?? 0;
  const ratingFinaleCalcolato = calcolaRating6Amico(base.votoIspettore, mediaCalcolata);

  return {
    ...base,
    votiDellePersone: recensioni,
    mediaVotoPersone,
    ratingFinaleCalcolato,
    inAttesaVotiQR,
  };
}
